import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useFormik } from 'formik'
import * as yup from 'yup'

import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Container,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { LocalizationProvider, StaticDateTimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import React, { ReactElement, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios, { pluckData } from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { selectRoles, selectUseEvents } from 'src/v3/settings/settings.slice'
import AddIcon from '@mui/icons-material/Add'
import {
  CalendarIcon,
  ClockIcon,
  CopyIcon,
  DeleteIcon,
  EditIcon,
  EventIcon,
  GroupIcon,
  LeftArrowIcon,
  LightbulbIcon,
  LocationIcon,
  RightArrowIcon,
  StudentIcon,
} from 'src/v3/core/icons'
import Loading from 'src/v3/components/loading'
import {
  fetchEvents,
  fetchSessions,
  fetchUsers,
  selectActiveEvent,
  selectEventById,
  selectEventsStatus,
  selectInvites,
  selectSessionsByEventID,
  selectSessionsStatus,
  selectUsersByLinkedService,
  selectUsersStatusByLinkedService,
} from './events.slice'

dayjs.extend(utc)

export default ({
  setStep,
  eventView,
}: {
  setStep: React.Dispatch<React.SetStateAction<'quiz' | 'sessions' | 'details'>>
  eventView: 'create' | 'edit'
}): ReactElement => {
  let dispatch = useAppDispatch()
  let { eventId } = useParams()

  let roles = useAppSelector(selectRoles)
  let useEvents = useAppSelector(selectUseEvents)
  let isAdmin = roles.includes('admin')

  let event = eventId
    ? useAppSelector(selectEventById(eventId))
    : useAppSelector(selectActiveEvent)
  let lsid = event?.linkedServiceID || ''
  let eventsStatus = useAppSelector(selectEventsStatus)
  let sessions = useAppSelector(selectSessionsByEventID(event?.id || ''))
  let sessionsStatus = useAppSelector(selectSessionsStatus)
  let users = useAppSelector(selectUsersByLinkedService(lsid))
  let usersStatus = useAppSelector(selectUsersStatusByLinkedService(lsid))
  let invites = useAppSelector(selectInvites)

  let [view, setView] = useState<'view' | 'create' | 'edit'>('view')
  let [userFilter, setUserFilter] = useState<string>('')
  let [userPage, setUserPage] = useState<number>(1)
  let [checkedUsers, setCheckedUsers] = useState<string[]>([])
  let [loading, setLoading] = useState<boolean>(false)
  let [copyMessage, setCopyMessage] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      id: yup.string(),
      instructor: yup.string().required(),
      sessionTime: yup.string().required(),
    }),
    initialValues: {
      id: '',
      instructor: '',
      sessionTime: '',
    },
    onSubmit: (values, { resetForm }) => {
      setLoading(true)
      let body = {
        sessionTime: values.sessionTime,
        instructor: values.instructor,
        userIDs: checkedUsers,
      }
      let request = !values.id
        ? axios.post('/api/event/session', { eventID: event?.id, ...body })
        : axios.put('/api/event/session', { id: values.id, ...body })
      request
        .then(pluckData)
        .then(() => dispatch(fetchSessions()))
        .then(() => resetForm())
        .then(() => {
          setUserPage(1)
          setUserFilter('')
          setView('view')
        })
        .catch((err) =>
          setErrorMessage(err?.response?.data || `Session ${view} failed`),
        )
        .finally(() => setLoading(false))
    },
  })

  let usersPerPage = 10

  let invitedUsers = invites[form.values.id]?.map((inv) => inv.userID) || []

  let filteredUsers =
    users
      ?.filter(
        (u) =>
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(userFilter) ||
          u.email.toLowerCase().includes(userFilter),
      )
      .sort(
        (a, b) =>
          Number(invitedUsers.includes(b.id)) -
            Number(invitedUsers.includes(a.id)) ||
          a.firstName.localeCompare(b.firstName),
      ) || []

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (sessionsStatus === 'uninitialized') {
      dispatch(fetchSessions())
    }
  }, [sessionsStatus])

  useEffect(() => {
    if (lsid && (!usersStatus || usersStatus === 'uninitialized')) {
      dispatch(fetchUsers(lsid))
    }
  }, [usersStatus])

  return useEvents ? (
    <Container maxWidth="xl">
      {(view === 'create' || view === 'edit') && (
        <form onSubmit={form.handleSubmit} style={{ marginTop: 16 }}>
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {view === 'create' ? 'Add New' : 'Edit'} Session
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Schedule event sessions and configure session details
              </Typography>
            </Box>
            <Button
              sx={{
                color: 'text.primary',
                textTransform: 'none',
                alignSelf: 'flex-start',
              }}
              startIcon={<LeftArrowIcon />}
              onClick={() => {
                form.resetForm()
                setCheckedUsers([])
                setView('view')
              }}
            >
              Back to Sessions
            </Button>
          </Box>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDateTimePicker
              sx={{
                height: 565,
                backgroundColor: 'background.paper',
              }}
              disablePast
              ampm={false}
              onAccept={(val: any) =>
                form.setFieldValue('sessionTime', dayjs(val).format())
              }
              value={dayjs(form.values.sessionTime)}
            />
          </LocalizationProvider>
          <TextField
            sx={{
              mt: 1,
              bgcolor: 'background.paperDark',
              '& .MuiInputBase-input': {
                color: 'text.secondary',
                cursor: 'default',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'gray.800',
              },
            }}
            slotProps={{ input: { readOnly: true } }}
            value={
              form.values.sessionTime
                ? dayjs(form.values.sessionTime).format('MMMM DD, YYYY @ H:mm')
                : 'Session Time'
            }
            fullWidth
            variant="outlined"
            name="sessionTime"
            error={form.touched.sessionTime && !!form.errors.sessionTime}
          />
          <Box sx={{ height: 24 }}></Box>
          {isAdmin ? ( // TODO: should this be based on event LSID too or instead?
            <FormControl fullWidth>
              <InputLabel id="instructor-label">Instructor</InputLabel>
              <Select
                sx={{ bgcolor: 'background.paperDark' }}
                value={form.values.instructor}
                label="Instructor"
                labelId="instructor-label"
                name="instructor"
                onChange={form.handleChange}
                error={form.touched.instructor && !!form.errors.instructor}
              >
                <MenuItem value="">Unselected</MenuItem>
                <MenuItem value="Tyler Christifulli">
                  Tyler Christifulli
                </MenuItem>
                <MenuItem value="Sam Ireland">Sam Ireland</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <TextField
              sx={{ bgcolor: 'background.paperDark' }}
              fullWidth
              variant="outlined"
              value={form.values.instructor}
              label="Instructor"
              name="instructor"
              onChange={form.handleChange}
              error={form.touched.instructor && !!form.errors.instructor}
            />
          )}
          {event?.linkedServiceID && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Invite Users
              </Typography>
              <TextField
                sx={{ bgcolor: 'background.paperDark', my: 0.5 }}
                size="small"
                fullWidth
                variant="outlined"
                value={userFilter}
                label="Search Users"
                onChange={(e) => {
                  setUserPage(1)
                  setUserFilter(e.target.value)
                }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {filteredUsers
                  ?.slice(
                    (userPage - 1) * usersPerPage,
                    userPage * usersPerPage,
                  )
                  .map((u) => (
                    <Card
                      key={u.id}
                      sx={{
                        py: 0.5,
                        px: 1.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <Checkbox
                        sx={{ ml: -1 }}
                        checked={checkedUsers.includes(u.id)}
                        onChange={() =>
                          setCheckedUsers(
                            checkedUsers.includes(u.id)
                              ? checkedUsers.filter((cu) => cu !== u.id)
                              : [...checkedUsers, u.id],
                          )
                        }
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography>
                          {u.firstName} {u.lastName}
                        </Typography>
                        <Typography variant="subtitle2" color="textSecondary">
                          {u.email}
                        </Typography>
                      </Box>
                      {invites[form.values.id]?.some(
                        (inv) => inv.userID === u.id,
                      ) && <Chip size="small" label="Invited" />}
                    </Card>
                  ))}
              </Box>
              {!filteredUsers.length && (
                <Typography>No users match this search.</Typography>
              )}
              <Pagination
                sx={{ mt: 0.5, display: 'flex', justifyContent: 'center' }}
                page={userPage}
                onChange={(_, value) => setUserPage(value)}
                count={Math.ceil(filteredUsers.length / usersPerPage)}
                variant="outlined"
                size="small"
              />
            </Box>
          )}
          <Box
            sx={{
              mt: 5,
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Button
              sx={{ color: 'text.primary', textTransform: 'none' }}
              startIcon={<LeftArrowIcon />}
              onClick={() => setStep('details')}
            >
              Previous: Event Details
            </Button>
            <Box sx={{ flexGrow: '1' }}></Box>
            <Button
              sx={{ color: 'text.primary', textTransform: 'none' }}
              variant="contained"
              type="submit"
            >
              Save Session
            </Button>
            <Button
              sx={{ color: 'text.primary', textTransform: 'none' }}
              endIcon={<RightArrowIcon />}
              onClick={() => setStep('quiz')}
            >
              Next: Quiz
            </Button>
          </Box>
        </form>
      )}
      {view === 'view' &&
        (!sessions.length ? (
          <>
            <Box
              sx={{ my: 5, display: 'flex', justifyContent: 'space-between' }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {event?.name}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  Manage your event sessions
                </Typography>
              </Box>
              <Button
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  my: 1,
                  px: 2,
                }}
              >
                Save Draft
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box
                sx={{
                  p: 4,
                  bgcolor: 'background.paperDark',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <Avatar
                  sx={{ height: 72, width: 72, bgcolor: 'background.paper' }}
                >
                  <EventIcon fontSize="large" color="primary" />
                </Avatar>
                <Typography variant="h6">No Sessions Added Yet</Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  textAlign="center"
                  sx={{ maxWidth: 450, lineHeight: '16px' }}
                >
                  Create your first session by clicking the button below. You
                  can add multiple sessions for different days or topics.
                </Typography>
                <Button
                  sx={{ py: 1.25, mt: 1.5 }}
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => setView('create')}
                >
                  Add First Session
                </Button>
              </Box>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paperDark',
                }}
              >
                <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                  <LightbulbIcon color="primary" />
                  <Typography>Tips for Creating Sessions</Typography>
                </Box>{' '}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                    gap: 2,
                  }}
                >
                  <Box>
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'flex',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <ClockIcon />
                      <Typography>Flexible Scheduling</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Set up multiple sessions across different days and times
                      to accommodate various schedules
                    </Typography>
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'flex',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <LocationIcon />
                      <Typography>Location Options</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Choose between in-person or virtual sessions with
                      automatic link generation.
                    </Typography>
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        color: 'primary.main',
                        display: 'flex',
                        gap: 0.5,
                        mb: 1,
                      }}
                    >
                      <GroupIcon />
                      <Typography>Attendee Management</Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      Manage RSVPs for each individual session.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                mt: 5,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Button
                sx={{ color: 'text.primary', textTransform: 'none' }}
                startIcon={<LeftArrowIcon />}
                onClick={() => setStep('details')}
              >
                Back to Event Details
              </Button>
              <Button
                sx={{ color: 'text.primary', textTransform: 'none' }}
                endIcon={<RightArrowIcon />}
                onClick={() => setStep('quiz')}
              >
                Next: Quiz
              </Button>
            </Box>
          </>
        ) : (
          <>
            {eventView === 'create' ? (
              <Box
                sx={{
                  mt: 5,
                  mb: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {event?.name}
                  </Typography>
                  <Typography variant="subtitle1" color="textSecondary">
                    Manage your event sessions
                  </Typography>
                </Box>
                <Box>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setView('create')}
                  >
                    Add Session
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  mt: -3,
                  mb: 2,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setView('create')}
                >
                  Add Session
                </Button>
              </Box>
            )}
            <Box sx={{ height: 600, overflow: 'scroll' }}>
              {sessions.map((session, i) => (
                <Card
                  key={session.id}
                  variant="outlined"
                  sx={{
                    bgcolor: 'background.paperDark',
                    py: 2.5,
                    pr: 2,
                    pl: 4,
                    mb: sessions.length === i + 1 ? 0 : 3,
                  }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography sx={{ fontSize: 18, mb: 1 }}>
                      Session {i + 1}
                    </Typography>
                    <Box>
                      <IconButton
                        onClick={() => {
                          form.setFieldValue('id', session.id)
                          form.setFieldValue('instructor', session.instructor)
                          form.setFieldValue('sessionTime', session.sessionTime)
                          setCheckedUsers(
                            invites[session.id]?.map((inv) => inv.userID) || [],
                          )
                          setView('edit')
                        }}
                      >
                        <EditIcon sx={{ height: 18, width: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setLoading(true)
                          axios
                            .delete(
                              `/api/event/session?sessionID=${session.id}`,
                            )
                            .then(() => dispatch(fetchSessions()))
                            .catch((err) =>
                              setErrorMessage(
                                err?.message || 'Error Deleting Session',
                              ),
                            )
                            .finally(() => setLoading(false))
                        }}
                      >
                        <DeleteIcon sx={{ height: 18, width: 18 }} />
                      </IconButton>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      fontSize: 14,
                      alignItems: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Box>
                      <CalendarIcon
                        sx={{ height: 18, width: 18, mb: '2px', mr: '4px' }}
                      />
                      <Box component="span" color="text.secondary">
                        {dayjs(session.sessionTime).format('MMMM DD, YYYY')}
                      </Box>
                    </Box>
                    <Box>
                      <ClockIcon
                        sx={{ height: 18, width: 18, mb: '2px', mr: '2px' }}
                      />
                      <Box component="span" color="text.secondary">
                        {dayjs(session.sessionTime).format('h:mm A')}
                        {event?.hours &&
                          ` - ${dayjs(session.sessionTime)
                            .add(event?.hours, 'hours')
                            .format('h:mm A')}`}
                      </Box>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      columnGap: 1.5,
                      fontSize: 14,
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <LocationIcon
                        color="primary"
                        sx={{ height: 18, width: 18, mb: '2px', mr: '2px' }}
                      />
                      <Box component="span">{event?.eventType}</Box>
                    </Box>
                    <Box>
                      <StudentIcon
                        color="primary"
                        sx={{ height: 18, width: 18, mb: '2px', mr: '6px' }}
                      />
                      <Box component="span">{session.instructor}</Box>
                    </Box>
                    <Box>
                      {event?.location.kind !== 'virtual' && (
                        <>
                          <Typography color="textSecondary">
                            {event?.location?.name}
                          </Typography>
                        </>
                      )}
                      {event?.location.kind !== 'inPerson' && (
                        <>
                          <Button
                            color="primary"
                            startIcon={
                              <CopyIcon
                                sx={{
                                  height: 18,
                                  width: 18,
                                  ml: '2px',
                                  mr: '-2px',
                                }}
                              />
                            }
                            sx={{ textTransform: 'none', ml: '-4px' }}
                            onClick={() => {
                              navigator.clipboard.writeText(
                                event?.location.kind !== 'inPerson'
                                  ? event?.location.url || ''
                                  : '',
                              )
                              setCopyMessage(true)
                            }}
                          >
                            Copy Link
                          </Button>
                          <Snackbar
                            color="secondary.main"
                            message="Copied to Clipboard"
                            autoHideDuration={2000}
                            onClose={() => setCopyMessage(false)}
                            open={copyMessage}
                          >
                            <Alert color="info">Copied to Clipboard</Alert>
                          </Snackbar>
                        </>
                      )}
                    </Box>
                    <Box>
                      <GroupIcon
                        color="primary"
                        sx={{ height: 18, width: 18, mb: '2px', mr: '6px' }}
                      />
                      <Box
                        component="span"
                        sx={{ fontSize: 14, color: 'text.secondary' }}
                      >
                        {invites[session.id]?.length} Attendees
                      </Box>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
            <Box
              sx={{
                mt: 5,
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Button
                sx={{ color: 'text.primary', textTransform: 'none' }}
                startIcon={<LeftArrowIcon />}
                onClick={() => setStep('details')}
              >
                Previous: Event Details
              </Button>
              <Button
                sx={{ color: 'text.primary', textTransform: 'none' }}
                endIcon={<RightArrowIcon />}
                onClick={() => setStep('quiz')}
              >
                Next: Quiz
              </Button>
            </Box>
          </>
        ))}
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <Loading
        entities={[eventsStatus, sessionsStatus, usersStatus]}
        loading={loading}
      />
    </Container>
  ) : (
    <></>
  )
}
